import _ from 'underscore';
import { useCallback, useState } from 'react';
import { LinkedInAudienceStatus, LinkedInAudienceType, LinkedInAudienceTypeCriterion } from './consts';

const LinkedInAudienceTypeCriterionToViewModel = {
  [LinkedInAudienceType.COMPANY_NAMES]: ({ Id, Criterion }) => ({
    id: String(Criterion.ProfileId),
    name: Criterion.CompanyName,
    industry: Criterion.Industry,
    size: Criterion.Size,
    logo: Criterion.LogoUrl,
    criterionId: Id,
  }),
  [LinkedInAudienceType.COMPANY_INDUSTRIES]: ({ Id, Criterion }) => ({
    id: String(Criterion.ProfileId),
    name: Criterion.Industry,
    criterionId: Id,
  }),
  [LinkedInAudienceType.JOB_FUNCTIONS]: ({ Id, Criterion }) => ({
    id: String(Criterion.ProfileId),
    name: Criterion.JobFunction,
    criterionId: Id,
  }),
  [LinkedInAudienceType.JOB_TITLES]: ({ Id, Criterion }) => ({
    id: String(Criterion.ProfileId),
    name: Criterion.JobTitle,
    criterionId: Id,
  }),
  [LinkedInAudienceType.JOB_SENIORITIES]: ({ Id, Criterion }) => ({
    id: String(Criterion.ProfileId),
    name: Criterion.JobSeniority,
    criterionId: Id,
  }),
};

const mapAudienceCriterionsToViewModel = criterions =>
  _.reduce(_.values(LinkedInAudienceType), (acc, audienceType) => {
    const criterionType = LinkedInAudienceTypeCriterion[audienceType];
    const criterionToViewModel = LinkedInAudienceTypeCriterionToViewModel[audienceType];

    const matchingCriterions = _.filter(criterions, criterion => criterion.Criterion.Type === criterionType);
    const mappedCriterions = _.map(matchingCriterions, criterionToViewModel);

    return { ...acc, [audienceType]: mappedCriterions };
  }, {});

const mapAudienceViewModelToBulkModel = (audienceType, targets, campaignId, status) =>
  targets.map(target => ({
    Campaign: { Id: campaignId },
    CriterionType: LinkedInAudienceTypeCriterion[audienceType],
    Status: status,
    Criterion: {
      Type: LinkedInAudienceTypeCriterion[audienceType],
      [`${LinkedInAudienceTypeCriterion[audienceType]}Criterion`]: {
        Target: Number(target.id),
      },
    },
    ...(status === LinkedInAudienceStatus.Deleted && { Id: target.criterionId }),
  }));

export const useTargetingAudiencesAdapter = ({ campaignId }) => {
  const [internalAudienceTargets, setInternalAudienceTargets] = useState({});

  const mapToViewModel = useCallback((criterions) => {
    const initialAudienceTargets = mapAudienceCriterionsToViewModel(criterions);

    setInternalAudienceTargets(initialAudienceTargets);

    return initialAudienceTargets;
  }, []);

  const mapToBulkModel = useCallback((audienceTargets) => {
    const idsBefore = _.chain(internalAudienceTargets)
      .map(targets => _.pluck(targets, 'id'))
      .flatten()
      .value();

    const idsAfter = _.chain(audienceTargets)
      .map(targets => _.pluck(targets, 'id'))
      .flatten()
      .value();

    const idsToAdd = _.difference(idsAfter, idsBefore);
    const idsToDelete = _.difference(idsBefore, idsAfter);

    const targetsToAdd = _.chain(audienceTargets)
      .mapObject(targets => _.filter(targets, target => idsToAdd.includes(target.id)))
      .map((targets, audienceType) => mapAudienceViewModelToBulkModel(audienceType, targets, campaignId, LinkedInAudienceStatus.Active))
      .flatten()
      .value();

    const targetsToDelete = _.chain(internalAudienceTargets)
      .mapObject(targets => _.filter(targets, target => idsToDelete.includes(target.id)))
      .map((targets, audienceType) => mapAudienceViewModelToBulkModel(audienceType, targets, campaignId, LinkedInAudienceStatus.Deleted))
      .flatten()
      .value();

    return _.union(targetsToAdd, targetsToDelete);
  }, [campaignId, internalAudienceTargets]);

  return {
    mapToViewModel,
    mapToBulkModel,
  };
};
